import { Dispatch, SetStateAction } from "react";
import { Box, Grid2 } from "@mui/material";
import { MetaData } from "../../../Login/typings/login.types";
import { CompanyCardSettingDialogHeader } from "./CompanyCard-Setting-DialogHeader.component";
import { AdvancedBookingAccordion } from "./CompanyCard-Setting-AdvancedAccordion.partial";
import { CheckInPoliciesAccordion } from "./CompanyCard-Setting-CheckinPolicyAccordion.partial";
import { OtherSettingsAccordion } from "./CompanyCard-Setting-OtherSettingAccordion.partial";
import {
  AdvancedBookingSettingsGroup,
  AdvancedBookingSettingsSetters,
  CheckinPolicyGroup
} from "../CompanyCard.types";
import { FileObject } from "../../../FileDropzone/typings/FileObject.type";
import { BookingSettingsAccordion } from "./CompanyCard-Setting-BookingSettingAccordion.partial";
import { BookingTypeEnablingAccordion } from "./CompanyCard-Setting-BookingTypeEnablingAccordion.partial";
import { IndependentBookingsAccordion } from "./CompanyCard-Setting-IndependentBookingsAccordion.partial";
import { getBookingTypeEnabled } from "./company-card-booking-type-enabling.config";

export type CompanyCardSettingContentProps = {
  newValues: MetaData;
  setNewValues: Dispatch<SetStateAction<MetaData>>;
  /** When omitted (e.g. create flow), bookingNormHours and maxAllowedNoBookingTime fall back to newValues with defaults. */
  companyMetaData?: MetaData;
  companyName: string | null;
  brandColor?: string | null;
  logoUrl: string | null;
  logoFile: FileObject[] | undefined;
  onEditLogo: () => void;
  newSetting: AdvancedBookingSettingsGroup;
  setNewSetting: AdvancedBookingSettingsSetters;
  checkinEnabledGrp: CheckinPolicyGroup;
  setCheckinEnabledGrp: Dispatch<SetStateAction<CheckinPolicyGroup>>;
  /** When true, do not render the header (e.g. create dialog provides its own). */
  skipHeader?: boolean;
  homeOffice: { homeOfficeDays: [number, number]; enabled: boolean; mandatory: boolean };
  setHomeOffice: Dispatch<
    SetStateAction<{ homeOfficeDays: [number, number]; enabled: boolean; mandatory: boolean }>
  >;
  /** When provided (create flow), passed to header for editable company name. */
  setCompanyName?: (name: string) => void;
  /** When provided (create flow), passed to header for logo file upload. */
  setLogoFile?: Dispatch<SetStateAction<FileObject[] | undefined>>;
  /** When provided (edit flow), passed to header for delete company action. */
  onDeleteCompany?: () => void;
};

export function CompanyCardSettingContent({
  newValues,
  setNewValues,
  companyMetaData,
  companyName,
  brandColor,
  logoUrl,
  logoFile,
  onEditLogo,
  newSetting,
  setNewSetting,
  checkinEnabledGrp,
  setCheckinEnabledGrp,
  homeOffice,
  setHomeOffice,
  skipHeader = false,
  setCompanyName,
  setLogoFile,
  onDeleteCompany
}: Readonly<CompanyCardSettingContentProps>) {
  const bookingNormHours = companyMetaData?.bookingNormHours ?? newValues.bookingNormHours ?? 8;
  const maxAllowedNoBookingTime =
    companyMetaData?.maxAllowedNoBookingTime ?? newValues.maxAllowedNoBookingTime ?? 20;
  const workplaceBookingEnabled = getBookingTypeEnabled(newValues, "workplaceBookingEnabled");

  return (
    <Grid2 container spacing={3}>
      {!skipHeader && (
        <Grid2 size={12}>
          <CompanyCardSettingDialogHeader
            newValues={newValues}
            companyName={companyName}
            brandColor={brandColor}
            logoUrl={logoUrl}
            logoFile={logoFile}
            setNewValues={setNewValues}
            onEditLogo={onEditLogo}
            setCompanyName={setCompanyName}
            setLogoFile={setLogoFile}
            onDeleteCompany={onDeleteCompany}
          />
        </Grid2>
      )}

      <Grid2 size={{ xs: 12, md: 6 }}>
        <Box display="flex" flexDirection="column" gap={2}>
          <BookingTypeEnablingAccordion newValues={newValues} setNewValues={setNewValues} />

          <IndependentBookingsAccordion newValues={newValues} setNewValues={setNewValues} />

          <AdvancedBookingAccordion
            newSetting={newSetting}
            setNewSetting={setNewSetting}
            newValues={newValues}
            setNewValues={setNewValues}
          />

          <CheckInPoliciesAccordion
            checkinEnabledGrp={checkinEnabledGrp}
            setCheckinEnabledGrp={setCheckinEnabledGrp}
            newSetting={newSetting}
            setNewSetting={setNewSetting}
            newValues={newValues}
            setNewValues={setNewValues}
          />
        </Box>
      </Grid2>

      <Grid2 size={{ xs: 12, md: 6 }}>
        <Box display="flex" flexDirection="column" gap={2}>
          <BookingSettingsAccordion
            newValues={newValues}
            setNewValues={setNewValues}
            bookingNormHours={bookingNormHours}
            maxAllowedNoBookingTime={maxAllowedNoBookingTime}
            homeOffice={homeOffice}
            setHomeOffice={setHomeOffice}
            workplaceBookingEnabled={workplaceBookingEnabled}
          />

          <OtherSettingsAccordion newValues={newValues} setNewValues={setNewValues} />
        </Box>
      </Grid2>
    </Grid2>
  );
}
